import type { ReactNode } from 'react'
import { PageBootSkeleton } from '@/components/layout/PageBootSkeleton'
import { EmptyVisual } from '@/components/shared/empty-states/EmptyVisual'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useOrganization } from '@/hooks/useOrganization'

import type { FeatureFlagKeyType } from '@scrambl/shared'

interface FeatureFlagGuardProps {
  flagKey: FeatureFlagKeyType
  children: ReactNode
}

/**
 * Route guard that checks if a feature flag is enabled for the current organization.
 * When the flag is off it renders a "not enabled" state in place, so a pasted or
 * bookmarked link explains itself instead of silently bouncing to Home.
 */
export function FeatureFlagGuard({ flagKey, children }: FeatureFlagGuardProps) {
  const { isLoading, checkFeatureFlag } = useFeatureFlags([flagKey])
  const { currentOrganization } = useOrganization()

  // A page in flight keeps the shape of the page it becomes, never a bare
  // centred spinner (design audit S12).
  if (isLoading) {
    return <PageBootSkeleton />
  }

  if (!checkFeatureFlag(flagKey)) {
    const orgSlug = currentOrganization?.slug
    return (
      <EmptyVisual
        shape="icon"
        tone="locked"
        line="This module isn't enabled for your workspace."
        hint="Ask an admin if you need it."
        action={{
          label: 'Back to Home',
          href: orgSlug ? `/${orgSlug}/today` : '/organizations',
        }}
        fullScreen
      />
    )
  }

  return <>{children}</>
}
